import { useGame } from "../store";
import type { CSSProperties } from "react";
import type { ActionView } from "../types";

function riskTone(probability: number): { label: string; cls: string } {
  if (probability >= 0.85) return { label: "稳", cls: "safe" };
  if (probability >= 0.65) return { label: "可搏", cls: "even" };
  if (probability >= 0.4) return { label: "险", cls: "risky" };
  return { label: "危", cls: "dire" };
}

type ActionEcho = {
  key: "help" | "harm";
  tone: "help" | "harm";
  label: string;
  points: number;
  sources: string;
  mixed: boolean;
};

function actionEchoes(action: ActionView): ActionEcho[] {
  const grouped = action.pointBreakdown.reduce(
    (acc, item) => {
      const contributions = pointSourceContributions(item);
      for (const contribution of contributions) {
        if (contribution.points === 0) continue;
        const tone = contribution.points > 0 ? "help" : "harm";
        const bucket = acc[tone];
        bucket.points += contribution.points;
        bucket.labels.push(contribution.label);
      }
      return acc;
    },
    {
      help: { points: 0, labels: [] as string[] },
      harm: { points: 0, labels: [] as string[] },
    },
  );
  const mixed = grouped.help.points > 0 && grouped.harm.points < 0;
  return (["help", "harm"] as const)
    .map((tone) => {
      const bucket = grouped[tone];
      if (!bucket.points || bucket.labels.length === 0) return null;
      return {
        key: tone,
        tone,
        label: mixed ? "旧物交织" : "旧物牵动",
        points: bucket.points,
        sources: formatSources(bucket.labels),
        mixed,
      };
    })
    .filter((echo): echo is ActionEcho => echo != null);
}

function signed(n: number): string {
  return n > 0 ? `+${n}` : String(n);
}

function pointSourceContributions(item: ActionView["pointBreakdown"][number]): Array<{ label: string; points: number }> {
  if (item.sourceContributions?.length) {
    return item.sourceContributions.map((source) => ({
      label: source.sourceLabel,
      points: source.points,
    }));
  }
  if (!item.sourceLabels?.length || item.points === 0) return [];
  return [{ label: formatSources(item.sourceLabels), points: item.points }];
}

function formatSources(labels: string[]): string {
  const unique = [...new Set(labels)].slice(0, 2);
  return labels.length > 2 ? `${unique.join(" / ")} 等` : unique.join(" / ");
}

export function ActionList() {
  const { view, selectedAction, previewAction, busy } = useGame();
  if (!view || view.status === "ended") return null;

  // Once the server has accepted a choice, lock the action list until resolved.
  const locked = view.pending != null;

  return (
    <div className={`actions ${locked ? "locked" : ""}`}>
      {view.actions.map((a, index) => {
        const selected = (view.pending?.actionId ?? selectedAction?.id) === a.id;
        const risk = a.needsCheck ? riskTone(a.probability) : null;
        const echoes = actionEchoes(a);
        const echoClass = echoes.length ? `has-echo ${echoes.map((echo) => `echo-${echo.tone}`).join(" ")}` : "";
        return (
          <button
            key={a.id}
            className={`action-card ${selected ? "selected" : ""} ${echoClass}`}
            style={{ "--i": index } as CSSProperties}
            disabled={busy || locked}
            onClick={() => previewAction(a)}
          >
            <div className="action-name">
              <span className="action-index">{String(index + 1).padStart(2, "0")}</span>
              <span className="action-title">{a.name}</span>
              {risk ? (
                <span className={`risk-chip ${risk.cls}`}>
                  {risk.label} {Math.round(a.probability * 100)}%
                </span>
              ) : (
                <span className="risk-chip free">无检定</span>
              )}
            </div>
            {a.des && <div className="action-des">{a.des}</div>}
            {echoes.length > 0 && (
              <div className="action-echoes" aria-label="旧物对该行动的影响">
                {echoes.map((echo) => (
                  <span key={echo.key} className={`action-echo ${echo.tone} ${echo.mixed ? "mixed" : ""}`}>
                    {echo.label} {signed(echo.points)} · {echo.sources}
                  </span>
                ))}
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}
